import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { ResearchReportPage } from "@/pages/research/ResearchReportPage";
import type { ResearchRecord } from "@/content/research";
import { breadcrumbJsonLd } from "@/config/seoConfig";
import { seoHead, SITE_BASE_URL } from "@/lib/seo/head";
import { orgRef, siteGraph, websiteRef } from "@/lib/seo/graph";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/research/$slug")({
  // Research text is loaded on demand so it stays out of every page's
  // up-front code (this route's config ships in the main bundle).
  loader: async ({ params }) => {
    const { RESEARCH_BY_SLUG, isDiscoverableResearch, isPublishedResearch } = await import("@/content/research");
    let record = RESEARCH_BY_SLUG[params.slug];
    if (!record || record.visibility !== "public") throw notFound();
    if (params.slug === "communication-skills-in-children") {
      const { COMMUNICATION_ARTICLE } = await import("@/content/research/communicationArticle");
      record = { ...record, ...COMMUNICATION_ARTICLE, relatedResources: record.relatedResources?.filter((resource) => resource.url !== "/courses/get-people-to-listen") };
    }
    return { record, published: isPublishedResearch(record), discoverable: isDiscoverableResearch(record) };
  },
  head: ({ loaderData: data }) => {
    if (!data) return seoHead({ title: "Research report not found", noindex: true });
    const { published, discoverable } = data;
    const loaderData = data.record;
    const path = `/research/${loaderData.slug}`;
    const base = seoHead({
      title: loaderData.seoTitle ?? loaderData.shortTitle,
      description: loaderData.seoDescription ?? loaderData.summary.slice(0, 155),
      ...(loaderData.ogImage ? { ogImage: loaderData.ogImage } : {}),
      canonical: discoverable ? path : undefined,
      noindex: !discoverable,
      follow: !discoverable,
      pageType: loaderData.researchType === "evidence_guide" ? "Article" : "WebPage",
      ogType: loaderData.researchType === "evidence_guide" ? "article" : "website",
      ...(published && loaderData.datePublished
        ? { article: { publishedTime: loaderData.datePublished, modifiedTime: loaderData.dateUpdated ?? loaderData.datePublished, section: loaderData.category ?? "Research", ...(loaderData.bodyMarkdown ? { authorName: loaderData.authors[0]?.name } : {}) } }
        : {}),
      jsonLd: discoverable
        ? [
            breadcrumbJsonLd([
              { name: "Home", url: "/" },
              { name: "Research", url: "/research" },
              { name: loaderData.shortTitle, url: path },
            ]),
            researchSchema(loaderData, published),
            ...(loaderData.faqs?.length
              ? [{
                  "@type": "FAQPage",
                  "@id": `${SITE_BASE_URL}${path}#faq`,
                  mainEntity: loaderData.faqs.map((faq) => ({
                    "@type": "Question",
                    name: faq.q,
                    acceptedAnswer: { "@type": "Answer", text: faq.a },
                  })),
                }]
              : []),
          ]
        : undefined,
    });
    // This revision requests an exact title, without the shared brand suffix.
    if (loaderData.bodyMarkdown) {
      base.meta = base.meta.map((meta) => meta.title ? { title: loaderData.seoTitle ?? loaderData.title } : meta.property === "og:title" || meta.name === "twitter:title" ? { ...meta, content: loaderData.seoTitle ?? loaderData.title } : meta);
    }
    if (discoverable) return base;
    const url = `${SITE_BASE_URL}${path}`;
    return {
      ...base,
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify(siteGraph({ url, name: loaderData.title, description: loaderData.summary, pageType: "WebPage" }, [{
          "@type": "BreadcrumbList",
          "@id": `${url}#breadcrumb`,
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_BASE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Research", item: `${SITE_BASE_URL}/research` },
            { "@type": "ListItem", position: 3, name: loaderData.shortTitle, item: url },
          ],
        }])),
      }],
    };
  },
  errorComponent: ResearchReportError,
  notFoundComponent: ResearchReportNotFound,
  component: ResearchReportRoute,
});

function researchSchema(record: ResearchRecord, published: boolean) {
  if (!published) return null;
  const url = `${SITE_BASE_URL}/research/${record.slug}`;
  return {
    "@type": record.researchType === "evidence_guide" ? "Article" : record.researchType === "data_note" && (record.csvUrl || record.dataUrl) ? "Dataset" : "Report",
    "@id": `${url}#report`,
    name: record.title,
    description: record.seoDescription ?? record.summary,
    url,
    publisher: orgRef,
    ...(record.researchType === "evidence_guide" ? { author: record.bodyMarkdown && record.authors.length ? record.authors.map((author) => ({ "@type": "Person", name: author.name })) : orgRef, headline: record.h1 ?? record.title } : {}),
    ...(record.coverImage ? { image: record.coverImage } : {}),
    isPartOf: websiteRef,
    ...(record.datePublished ? { datePublished: record.datePublished } : {}),
    ...(record.dateUpdated ? { dateModified: record.dateUpdated } : {}),
    ...(record.populationDescription ? { about: record.populationDescription } : {}),
    ...(record.geography ? { spatialCoverage: record.geography } : {}),
    ...(record.findings.length ? { abstract: record.findings.map((finding) => finding.description).join(" ") } : {}),
    ...(record.pdfUrl || record.csvUrl || record.dataUrl
      ? { distribution: [record.pdfUrl, record.csvUrl, record.dataUrl].filter((value): value is string => Boolean(value)).map((contentUrl) => ({ "@type": "DataDownload", contentUrl })) }
      : {}),
  };
}

function ResearchReportRoute() {
  return <ResearchReportPage record={Route.useLoaderData().record} />;
}

function ResearchReportNotFound() {
  return (
    <main className="container mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="lx-h1 mb-3">Research report not found</h1>
      <p className="text-muted-foreground">This report is not available.</p>
      <Button asChild variant="outline" className="mt-6"><a href="/research">Back to Research</a></Button>
    </main>
  );
}

function ResearchReportError({ error: rawError, reset }: { error: unknown; reset: () => void }) { const error = rawError instanceof Error ? rawError : new Error(String(rawError));
  const router = useRouter();
  return (
    <main className="container mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="lx-h1 mb-3">Research page unavailable</h1>
      <p className="text-muted-foreground">{error.message || "The report could not be loaded."}</p>
      <Button className="mt-6" onClick={() => { void router.invalidate(); reset(); }}>Try again</Button>
    </main>
  );
}